import React, { useState } from 'react';
import './SMSEngagementTable.scss';

import { timeAgo, epochToDateTimeString, intlNumberFormat } from 'helpers/common.helper';
import TotalHeader from './Shared/TotalHeader';
import { ReactComponent as TwilioProvider } from 'assets/images/provider-twilio.svg';
import { ReactComponent as IconLastUpdated } from 'assets/icons/last-updated.svg';
import { ReactComponent as IconMessage } from 'assets/icons/message.svg';

import { FlightTable, FlightTextInput, getIcon } from '@flybits/design-system';
import DateRangePicker from 'components/Shared/DateRangePicker';
import type { Preset } from 'components/Shared/DateRangePicker';

const DATERANGE_PRESETS: Preset[] = ['today', 'last7', 'last30', 'last60', 'last90', 'custom'];
import StatusBadge, { STATUS_TYPES } from 'components/Shared/StatusBadge/StatusBadge';
import { AdvancedDateRange, DateRange } from 'pages/AnalyticsV2/types';
import { DATE_FORMAT_OPTIONS } from '../constants';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import useDebounce from 'hooks/useDebounce';
import useTableSort from 'hooks/useTableSort';
import AnalyticsExperiencesAPI from 'services/api/analytics-experiences.api';
import { formatTotalValue } from 'pages/AnalyticsV2/helpers';
import DownloadEngagementData from './DownloadEngagementData';
import { ENGAGEMENT_DATA_TYPES, SMSEngagementData, TableHeader } from 'components/Analytics/types';
import { useDispatch } from 'react-redux';
import useConfirmModal from 'hooks/useConfirmModal';
import { ConfirmationDialogProps } from 'components/Shared/shared.types';
import { ReactComponent as IconDownloadData } from 'assets/icons/icon-download-data.svg';

const MAIN_CLASS = 'sms-table';
const CLASSES = {
  HEADER: `${MAIN_CLASS}__header`,
  TITLE: `${MAIN_CLASS}__header__title`,
  SEARCH: `${MAIN_CLASS}__search`,
  SEARCH_WRAPPER: `${MAIN_CLASS}__search__wrapper`,
  DOWNLOAD: `${MAIN_CLASS}__header__download`,
  DATE_RANGE: `${MAIN_CLASS}__date-range`,
  DATE_RANGE_DD: `${MAIN_CLASS}__date-range__dropdown`,
  EMPTY_DATA: `${MAIN_CLASS}__empty-data`,
  SPINNER: `${MAIN_CLASS}__spinner`,
  PROVIDER: `${MAIN_CLASS}__provider`,
  LAST_UPDATED: `${MAIN_CLASS}__last-updated`,
  TABLE_WRAPPER: `${MAIN_CLASS}__table-wrapper`,
  TABLE_COMPLEX_NUMBER: `${MAIN_CLASS}__table__complex-number`,
};

const SMSTotalHeaders = ['TOTAL SENT', 'TOTAL DELIVERED', 'TOTAL FAILED', 'TOTAL UNDELIVERED'];

const analyticsExperiencesApi = new AnalyticsExperiencesAPI();

const fetchSMSEngagement = async (
  analyticsExperiencesApi: AnalyticsExperiencesAPI,
  page: number,
  perPage: number,
  dateRange: DateRange<Date>,
  search: string,
  sortBy: string,
  sortOrder: string,
): Promise<SMSEngagementData> => {
  const { data: tableRes } = await analyticsExperiencesApi.getSMSEngagement({
    start: Math.trunc((dateRange[0]?.getTime() ?? new Date().setHours(0, 0, 0, 0)) / 1000),
    end: Math.trunc((dateRange[1]?.getTime() ?? new Date().setHours(23, 59, 59, 999)) / 1000),
    offset: (page - 1) * perPage,
    limit: perPage,
    search,
    sortBy,
    sortOrder,
  });

  const { data: totals } = await analyticsExperiencesApi.getSMSTotals({
    start: Math.trunc((dateRange[0]?.getTime() ?? new Date().setHours(0, 0, 0, 0)) / 1000),
    end: Math.trunc((dateRange[1]?.getTime() ?? new Date().setHours(23, 59, 59, 999)) / 1000),
  });

  return {
    entities: tableRes.entities || [],
    pagination: tableRes.pagination,
    updatedAt: tableRes.updated_at,
    totals: totals,
  };
};

const smsEngagementHeaders: TableHeader[] = [
  {
    name: <span>Name</span>,
    key: 'name',
    isVisible: true,
    isSortable: true,
    tooltip: 'The name of the experience',
  },
  {
    name: <span>Experience status</span>,
    key: 'journeyStatus',
    isVisible: true,
    isSortable: true,
    tooltip:
      'This is the status from the start of the experience . The possible statuses are; active, inactive, deleted.',
  },
  {
    name: <span>Sent</span>,
    key: 'sent',
    isVisible: true,
    isSortable: true,
    tooltip: 'The total number of emails sent from Flybits to Twilio',
  },
  {
    name: <span>Delivered</span>,
    key: 'delivered',
    isVisible: true,
    isSortable: true,
    tooltip: 'The total number of emails that have been successfully delivered from Flybits to Twilio',
  },
  {
    name: <span>Failed</span>,
    key: 'failed',
    isVisible: true,
    isSortable: true,
    tooltip: 'The total number of emails that failed to send from Flybits to Twilio',
  },
  {
    name: <span>Undelivered</span>,
    key: 'undelivered',
    isVisible: true,
    isSortable: true,
    tooltip: 'Twilio has received a delivery receipt indicating that the outbound message was not delivered.',
  },
];

const engagementRow = (entity: SMSEngagementData['entities'][number]) => {
  const statusBadgeType =
    Object.values(STATUS_TYPES).find((p) => p === (entity.journeyStatus as string)) || STATUS_TYPES.STATUS_ACTIVE;
  return {
    key: entity.id,
    name: entity.name,
    journeyStatus: <StatusBadge status={statusBadgeType} title={entity.journeyStatus} showTooltip={false} />,
    sent: intlNumberFormat(entity.metrics.sent),
    delivered: intlNumberFormat(entity.metrics.delivered),
    failed: intlNumberFormat(entity.metrics.failed),
    undelivered: intlNumberFormat(entity.metrics.undelivered),
  };
};

const confirmationDialogProps: ConfirmationDialogProps = {
  icon: <IconDownloadData />,
  title: 'Download SMS table',
  description: 'Note that the table will be downloaded based on your current filters. The format will be in CSV.',
  primaryAction: {
    value: 'Download',
  },
  secondaryAction: {
    value: 'Cancel',
  },
};

const SMSEngagementTable: React.FC = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(5);

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);
  const [dateRange, setDateRange] = useState<AdvancedDateRange>([startOfToday, new Date(), 0, 'day']);
  const [queryDateRange, setQueryDateRange] = useState<AdvancedDateRange>([startOfToday, endOfToday]);

  // Use the custom hook for sorting
  const { sortByColumn, sortOrder, handleHeaderSort } = useTableSort('name', 'desc');

  const { data, isFetching } = useQuery({
    queryKey: ['sms-engagement', currentPage, perPage, queryDateRange, debouncedSearch, sortByColumn, sortOrder],
    queryFn: () =>
      fetchSMSEngagement(
        analyticsExperiencesApi,
        currentPage,
        perPage,
        [queryDateRange[0], queryDateRange[1]],
        debouncedSearch,
        sortByColumn,
        sortOrder,
      ),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const SMSTotalValues = [
    formatTotalValue(data?.totals.sent ?? 0),
    formatTotalValue(data?.totals.delivered ?? 0),
    formatTotalValue(data?.totals.failed ?? 0),
    formatTotalValue(data?.totals.undelivered ?? 0),
  ];

  const handleDateRangeChange = (newDate: AdvancedDateRange) => {
    setDateRange(newDate);
    const hasFullRange = newDate[0] !== null && newDate[1] !== null;

    if (hasFullRange) {
      const endOfDay = new Date(newDate[1] ?? new Date());
      if (newDate.length < 3 || (newDate.length >= 3 && newDate[2] === 0)) {
        // Known interval - no custom or single day
        endOfDay.setHours(23, 59, 59, 999);
      }
      setQueryDateRange([newDate[0], endOfDay]);
    }
  };

  const handleSearchChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    setCurrentPage(1);
    setSearch(value);
  };

  const [smsDataForCSV, setSmsDataForCSV] = useState<Record<string, string>[]>([]);
  const [isAllSmsDataLoading, setAllSmsDataLoading] = useState(false);

  const [DownloadConfirmModal, showDownloadConfirmModal] = useConfirmModal(confirmationDialogProps);

  const generateCSVRecords = (data: SMSEngagementData): Record<string, string>[] => {
    return data.entities.map((smsEntity) => {
      return {
        Name: smsEntity.name,
        'Experience Status': smsEntity.journeyStatus,
        'Total Sent': smsEntity.metrics.sent.toLocaleString(),
        Delivered: smsEntity.metrics.delivered.toLocaleString(),
        Failed: smsEntity.metrics.failed.toLocaleString(),
        Undelivered: smsEntity.metrics.undelivered.toLocaleString(),
      };
    });
  };

  const handleClickDownload = async () => {
    try {
      if (await showDownloadConfirmModal()) {
        setAllSmsDataLoading(true);
        const allData = await queryClient.fetchQuery({
          queryKey: ['sms-engagement', queryDateRange, debouncedSearch, sortByColumn, sortOrder],
          queryFn: () =>
            fetchSMSEngagement(
              analyticsExperiencesApi,
              1,
              100000, // Sending higher limit to BE to fetch all records
              [queryDateRange[0], queryDateRange[1]],
              debouncedSearch,
              sortByColumn,
              sortOrder,
            ),
        });

        if (!allData.entities?.length) {
          dispatch({
            type: 'SHOW_SNACKBAR',
            payload: {
              title: 'Info',
              content: 'No SMS engagement data found',
              type: 'info',
            },
          });
        } else {
          setSmsDataForCSV(generateCSVRecords(allData));
        }
      }
    } catch {
      dispatch({
        type: 'SHOW_SNACKBAR',
        payload: {
          title: 'Error',
          content: 'Something went wrong and could not download SMS engagement data.',
          type: 'error',
        },
      });
    } finally {
      setAllSmsDataLoading(false);
    }
  };

  return (
    <div className={MAIN_CLASS}>
      <div className={CLASSES.HEADER}>
        <h2>
          <div className={CLASSES.TITLE}>
            <IconMessage aria-hidden="true" /> SMS
          </div>
          {data?.updatedAt ? (
            <div className={CLASSES.LAST_UPDATED}>
              <IconLastUpdated aria-hidden="true" /> Last updated {timeAgo(data.updatedAt * 1000)}
            </div>
          ) : null}
        </h2>
        <div className={CLASSES.DATE_RANGE}>
          {(dateRange[2] !== undefined || dateRange[3] === 'allTime') &&
            (dateRange[2] === 0
              ? `${epochToDateTimeString(new Date().getTime(), 'en-US', DATE_FORMAT_OPTIONS)}`
              : `${epochToDateTimeString(dateRange[0] || 0, 'en-US', DATE_FORMAT_OPTIONS)} - ${epochToDateTimeString(
                  dateRange[1] || 0,
                  'en-US',
                  DATE_FORMAT_OPTIONS,
                )}`)}
        </div>
        <div>
          <DateRangePicker
            className={CLASSES.DATE_RANGE_DD}
            value={dateRange}
            onChange={handleDateRangeChange}
            maxDate={new Date()}
            presets={DATERANGE_PRESETS}
            ariaLabel="Select SMS engagement date range"
          />
        </div>
        <div className={CLASSES.DOWNLOAD}>
          <DownloadEngagementData
            data={smsDataForCSV}
            type={ENGAGEMENT_DATA_TYPES.SMS}
            isDownloading={isAllSmsDataLoading}
            onClickDownload={handleClickDownload}
          />
        </div>
      </div>
      <TotalHeader headers={SMSTotalHeaders} values={SMSTotalValues} isLoading={isFetching} />
      <div className={CLASSES.SEARCH}>
        <div className={CLASSES.SEARCH_WRAPPER}>
          <FlightTextInput
            onChange={handleSearchChange}
            value={search}
            placeholderText="Search"
            iconInput="search"
            width="280px"
            hasClearIcon
          />
        </div>
        <div className={CLASSES.PROVIDER}>
          <TwilioProvider /> Data source by Twilio
        </div>
      </div>
      <div className={CLASSES.TABLE_WRAPPER}>
        <FlightTable
          tableHeaders={smsEngagementHeaders}
          isLoading={isFetching}
          handleHeaderSort={handleHeaderSort}
          hasPaginationBeforeTable={false}
          hasPaginationAfterTable={!!data?.entities?.length && !isFetching}
          tableData={data?.entities?.map(engagementRow)}
          sortOrder={sortOrder}
          paginationProps={{
            totalPageNumber: Math.ceil((data?.pagination?.totalRecords ?? 0) / perPage) || 1,
            currentPageNumber: currentPage,
            rowsPerPageOptions: [5, 10, 15, 20],
            currentRowsPerPage: perPage,
            handlePageChange: (page: number) => setCurrentPage(page),
            handleRowsPerPageChange: (num: number | string) => {
              setPerPage(Number(num));
              setCurrentPage(1);
            },
          }}
          sortByKey={sortByColumn}
        />
        {data?.entities?.length === 0 && !isFetching && (
          <div className={CLASSES.EMPTY_DATA}>
            {getIcon('search', {})}
            No results found
          </div>
        )}
      </div>
      <DownloadConfirmModal />
    </div>
  );
};

export default SMSEngagementTable;
